// Import React elements.
import { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

// Import context and API functions.
import { AuthContext } from "global/context/AuthContext";
import { getHelp } from "api/APIStrichCalls";

// Import shared components and icons.
import { LoadingSpinner } from "global/components/loading_ui/LoadingSpinnerAnimation";
import { CloseIcon, GlobalIconBase } from "global/strich_icons/StrichIcons";

// Import css stylesheet.
import "./HelpButton.css";



// Draw an open book to distinguish article help from information popovers.
const HelpBookIcon = () => (

    <GlobalIconBase size={17} strokeWidth={1.8}>
        <path d="M12 7v14" />
        <path d="M3 18V5a2 2 0 0 1 2-2h3a4 4 0 0 1 4 4 4 4 0 0 1 4-4h3a2 2 0 0 1 2 2v13h-5a4 4 0 0 0-4 3 4 4 0 0 0-4-3H3Z" />
    </GlobalIconBase>

);



// Render a reusable button which opens a published help article in a modal.
const HelpButton = ({ type = "help", section, code, label }) => {

    // Get the authenticated API token.
    const { token } = useContext(AuthContext);

    // Store modal visibility and article loading state.
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [article, setArticle] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");

    // Keep references to the trigger and modal close controls.
    const triggerRef = useRef(null);
    const closeRef = useRef(null);



    // Load the requested article when the popup opens.
    useEffect(() => {

        // Do not request content while the popup is closed.
        if (!isOpen) { return; }

        // Ignore responses after the popup closes or the requested article changes.
        let active = true;

        // Clear the previous article and start loading.
        setLoading(true);
        setArticle(null);
        setErrorMessage("");

        // Request only the specified article from the unified help endpoint.
        const loadArticle = async () => {

            try {

                // Get the published article within its section.
                const response = await getHelp(token, type, { section, code });

                // Ignore responses to superseded requests.
                if (!active) { return; }

                // Find the requested section and item in the grouped API response.
                const sections = response?.success && Array.isArray(response.results) ? response.results : [];
                const requestedSection = sections.find((item) => item.code === section);
                const requestedArticle = requestedSection?.items?.find((item) => item.code === code);

                // Show the article or a meaningful error.
                if (requestedArticle) {
                    setArticle(requestedArticle);
                } else {
                    setErrorMessage(response?.error_message || "This help article is not available.");
                }

            } catch (error) {

                // Report failed requests without closing the popup.
                if (active) { setErrorMessage("Unable to load this help article."); }

            } finally {

                // Stop loading only for the currently active request.
                if (active) { setLoading(false); }

            }

        };

        // Start the article request.
        loadArticle();

        // Ignore the request after the popup closes or changes article.
        return () => { active = false; };

    }, [isOpen, token, type, section, code]);



    // Handle modal focus, Escape and background scrolling.
    useEffect(() => {

        // Leave the page unchanged while the modal is closed.
        if (!isOpen) { return; }

        // Save the current page scroll setting and prevent background scrolling.
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        // Focus the modal close button on opening.
        closeRef.current?.focus();

        // Close the modal when Escape is pressed.
        const handleKeyDown = (event) => {

            if (event.key === "Escape") {
                setIsOpen(false);
                triggerRef.current?.focus();
            }

        };

        // Listen for the close shortcut.
        document.addEventListener("keydown", handleKeyDown);

        // Restore the page when the modal closes.
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = previousOverflow;
        };

    }, [isOpen]);



    // Open the article popup.
    const openHelp = () => {

        setLoading(true);
        setIsOpen(true);

    };

    // Close the article popup and restore focus to its trigger.
    const closeHelp = () => {

        setIsOpen(false);
        triggerRef.current?.focus();

    };

    // Close only when the overlay itself is clicked.
    const handleOverlayClick = (event) => {

        if (event.target === event.currentTarget) { closeHelp(); }

    };



    // Render the help icon and its modal when requested.
    return (

        <>

            <button
                ref={triggerRef}
                type="button"
                className="help-button"
                title={`Help: ${label}`}
                aria-label={`Open help for ${label}`}
                onClick={openHelp}
            >
                <HelpBookIcon />
            </button>

            {/* Render the popup outside the page's layout containers. */}
            {isOpen && createPortal(

                <div className="popup-overlay" onClick={handleOverlayClick}>

                    <div className="popup-container help-popup__container" role="dialog" aria-modal="true" aria-label={article?.title || label || "Help"}>

                        {/* Display the standard modal header. */}
                        <div className="popup__header">

                            <div className="popup__headerLeft">
                                <h2 className="popup__title">{article?.title || label || "Help"}</h2>
                            </div>

                            <div className="popup__headerRight">
                                <button ref={closeRef} type="button" className="popup__close" onClick={closeHelp} title="Close">
                                    <CloseIcon />
                                </button>
                            </div>

                        </div>

                        {/* Display the requested help article. */}
                        <div className="popup-content help-popup__body">

                            {loading ? (

                                <div className="help-popup__loading">
                                    <LoadingSpinner />
                                    <p>Loading help...</p>
                                </div>

                            ) : errorMessage ? (

                                <p role="alert">{errorMessage}</p>

                            ) : article ? (

                                <div className="help-popup__article" dangerouslySetInnerHTML={{ __html: article.content || "" }} />

                            ) : null}

                        </div>

                        {/* Offer a link to the same article in the full Help Centre. */}
                        {article && !loading && (
                            <div className="help-popup__footer">
                                <Link to={`/support?type=${encodeURIComponent(type)}&section=${encodeURIComponent(section)}&code=${encodeURIComponent(code)}`} onClick={closeHelp}>
                                    Open in Help Centre
                                </Link>
                            </div>
                        )}

                    </div>

                </div>,
                document.body

            )}

        </>

    );

};

export default HelpButton;